/**
 * ADS context (growth kit) — the only point where the app composes
 * provider + domain policy + capping + rewards.
 *
 * Decision chain, outside in:
 *   1. `resolveAdsConfig(env, overrides)` — flags **OFF by default**
 *      (`VITE_ADS_ENABLED`), with back-office overrides on top.
 *   2. `shouldShowAds(config, tier)` from the monetization kit — **premium ⇒ 0
 *      ads immediately** (optimistic local flag: no wait for sync).
 *   3. `makeAdProvider(...)` — with the preceding result `false`, returns the
 *      no-op and **loads no SDK** (zero network requests).
 *   4. **`evaluateCashsyncPlacement(...)`** — the money domain's HARD policy
 *      (`monetization/ads-policy`): no banner, never next to an editable
 *      amount, no behavioral targeting over financial data. It comes
 *      BEFORE capping on purpose: if the domain says no, the frequency
 *      schedule has no say.
 *   5. `adGate(...)` — decides whether it BELONGS here and now (capping + breakpoint).
 *
 * Guest-first: capping and rewards live in the device's `localStorage`,
 * so they work **without an account and offline**.
 */
import {
  type Accessor,
  createContext,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  type ParentComponent,
  useContext,
} from "solid-js";
import {
  adCapPolicyOverrides,
  AdCapsStore,
  adGate,
  type AdGateReason,
  type AdKind,
  type AdPolicyVerdict,
  type AdProvider,
  type AdReward,
  AdRewardsStore,
  adsAreLive,
  type AdsConfig,
  billingQueryKeys,
  CASHSYNC_AD_PLACEMENTS,
  type CashsyncPlacementId,
  type components,
  evaluateCashsyncPlacement,
  evaluateExportAllowance,
  type ExportAllowance,
  ExportBudgetStore,
  makeAdProvider,
  NoneAdProvider,
  placementDef,
  resolveAdsConfig,
  resolveMonetizationConfig,
  shouldShowAds,
} from "@cashsync/shared";
import { api, createApiQuery } from "@/lib/api";
import { appConfig } from "@/lib/app-config";
import { useSession } from "@/lib/auth";
import { adsEnv } from "./ads-env";

type ErrorModel = components["schemas"]["ErrorModel"];
type BillingConfig = components["schemas"]["BillingConfigResponse"];
type BillingUsage = components["schemas"]["BillingUsageResponse"];

/** Session signals that feed the gate (the app-shell moves them itself). */
interface SessionSignals {
  activeMs: number;
  unitsCompleted: number;
  isFirstEver: boolean;
}

/**
 * Combined decision. `reason` combines codes from the generic gate and the
 * domain policy: telemetry must distinguish "cap reached" from "there was an
 * amount being edited".
 */
interface CashsyncAdDecision {
  allow: boolean;
  reason: AdGateReason | AdPolicyVerdict["reason"];
}

export interface AdsContextValue {
  config: Accessor<AdsConfig>;
  /** Are ads live? (flags + tier + network). `false` ⇒ nothing mounts. */
  live: Accessor<boolean>;
  /** Is the user in an ad-free tier? (premium ⇒ 0 ads immediately). */
  premium: Accessor<boolean>;
  provider: Accessor<AdProvider>;
  /** Should this placement be shown NOW? (policy + capping). */
  gate: (placementId: CashsyncPlacementId, inFlight?: boolean) => CashsyncAdDecision;
  /** Records that a value unit was completed (reviewed month, closed report…). */
  completeValueUnit: () => void;
  /** Declares that a task is in progress (active input, partially filled form). */
  setInFlight: (value: boolean) => void;
  /**
   * Declares that an AMOUNT is being edited on screen. Besides counting as a
   * task in progress, this activates the hard `ads-policy` rule: while it is
   * `true`, no placement in this app can be shown.
   */
  setAmountBeingEdited: (value: boolean) => void;
  /** Runs an interstitial. Returns `true` if it was actually shown. */
  showInterstitial: (placementId: CashsyncPlacementId) => Promise<boolean>;
  /** Runs a rewarded ad. Returns the GRANTED reward, or `null`. */
  showRewarded: (placementId: CashsyncPlacementId) => Promise<AdReward | null>;
  /** Records that a rewarded offer was SHOWN (for backoff). */
  recordOffer: (placementId: CashsyncPlacementId) => void;
  /** Screen counters (native) — do not persist. */
  setScreenCounts: (counts: { natives?: number; banners?: number }) => void;
  rewards: AdRewardsStore;
  /** Daily export budget (raised by the `export-boost` rewarded ad). */
  exportBudget: ExportBudgetStore;
  /** Export verdict ready for the UI (unlimited with ads off). */
  exportAllowance: () => ExportAllowance;
  /** Records a consumed export. */
  recordExport: () => void;
  /** Changes when a reward is re-granted: use it to recompute derived values. */
  rewardsVersion: Accessor<number>;
}

const AdsContext = createContext<AdsContextValue>();

const FIRST_SESSION_KEY = "ads.session.seen";

/** Safe storage: in SSR/tests without a DOM, falls back to an in-memory map. */
function safeStorage(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

function memoryStorage() {
  const map = new Map<string, string>();
  return {
    getItem: (k: string) => map.get(k) ?? null,
    setItem: (k: string, v: string) => {
      map.set(k, v);
    },
    removeItem: (k: string) => {
      map.delete(k);
    },
  };
}

export const AdsProvider: ParentComponent = (props) => {
  const session = useSession();
  const storage = safeStorage() ?? memoryStorage();

  // This provider lives in the ENTIRE app, so the query is deliberately
  // cheap: long cache and ZERO retries. If there is no response (e.g. a
  // guest, for whom the endpoint requires session) the layer falls back to env
  // config, which defaults to OFF — never retrying in a loop against the
  // server rate-limiter for an ad.
  // CAREFUL (measured bug, P23): there was also a `retry: false` here. `staleTime` is
  // per OBSERVER, but `retry` belongs to the QUERY: by sharing the cache key
  // (`billingQueryKeys.config()`) with `PlanOverview` and the profile
  // landing page, this override reconfigured the other consumers at a DISTANCE and
  // left `/profile/plan` with ZERO retries → a transient 500 rendered the entire
  // error box instead of retrying (measured: 1 request and an error after
  // ~800 ms). What makes this query cheap is the 5-minute `staleTime`, not
  // removing retries: the QueryClient default is kept (retry: 2) so
  // that ALL consumers of the key behave the same.
  const configQuery = createApiQuery<BillingConfig, ErrorModel>(
    () => billingQueryKeys.config(),
    async () => api.GET("/api/billing/config", {}),
    () => ({ staleTime: 300_000 }),
  );

  const usageQuery = createApiQuery<BillingUsage, ErrorModel>(
    () => billingQueryKeys.usage(),
    async () => api.GET("/api/billing/usage", {}),
    () => ({
      staleTime: 30_000,
      enabled: !session.isVisitor() && configQuery.data?.enabled === true,
    }),
  );

  // Bundle env + overrides served by the back-office (env → override).
  const adsConfig = createMemo(() =>
    resolveAdsConfig(
      adsEnv(),
      Object.entries(configQuery.data?.ads ?? {}).map(([key, value]) => ({ key, value })),
    ),
  );

  /**
   * Monetization kit verdict. The visitor inherits `defaultTier`
   * (free ⇒ ads on), and premium disables ads immediately.
   */
  const monetizationSaysAds = createMemo(() => {
    const remote = configQuery.data;
    if (!remote) {
      // Guest-first: a visitor CANNOT be premium (there is no subscription
      // without an account), so it inherits the default tier — free, ad-supported.
      // For a signed-in user, however, we wait for the actual verdict: an
      // ad flash to someone paying is the worst of both worlds.
      return session.isVisitor();
    }
    const config = resolveMonetizationConfig({}, []);
    config.enabled = remote.enabled;
    config.defaultTier = remote.defaultTier;
    config.tiers = remote.tiers.map((tier) => ({
      id: tier.id,
      label: tier.label,
      adsEnabled: tier.adsEnabled,
      limits: {
        storage_bytes: tier.limits.storageBytes ?? null,
        items: tier.limits.maxItems ?? null,
        collections: tier.limits.maxCollections ?? null,
        orgs: tier.limits.maxOrgs ?? null,
        attachments: tier.limits.maxAttachments ?? null,
      },
    }));
    return shouldShowAds(config, usageQuery.data?.tier);
  });

  const live = createMemo(() => adsAreLive(adsConfig(), monetizationSaysAds()));
  const premium = createMemo(() => !monetizationSaysAds());

  // Always START with the no-op: until ads are confirmed
  // live, no request is even possible.
  const [provider, setProvider] = createSignal<AdProvider>(new NoneAdProvider());

  const scope = () => session.userId() || "guest";
  const caps = new AdCapsStore(storage, scope());
  const rewards = new AdRewardsStore(storage, scope());
  const exportBudget = new ExportBudgetStore(storage, scope());

  const startedAt = Date.now();
  const [unitsCompleted, setUnitsCompleted] = createSignal(0);
  const [inFlight, setInFlight] = createSignal(false);
  // Count of OPEN amount editors (not a boolean): two overlapping
  // dialogs cannot leave the flag `false` when the first one closes.
  const [amountEditors, setAmountEditors] = createSignal(0);
  const [rewardsVersion, setRewardsVersion] = createSignal(0);

  const isFirstEver = (() => {
    const seen = storage.getItem(FIRST_SESSION_KEY) !== null;
    storage.setItem(FIRST_SESSION_KEY, "1");
    return !seen;
  })();

  const sessionSignals = (): SessionSignals => ({
    activeMs: Date.now() - startedAt,
    unitsCompleted: unitsCompleted(),
    isFirstEver,
  });

  // The SDK loads only when ads are LIVE: while `live()` is
  // false, there is no real provider, so no request is possible.
  createEffect(() => {
    if (!live()) {
      setProvider(() => new NoneAdProvider());
      // After a premium purchase: 0 ads immediately ⇒ local capping is forgotten.
      caps.reset();
      return;
    }
    const next = makeAdProvider(adsConfig());
    void (async () => {
      // Intentionally no options: the provider HARDENS against its own config
      // (`hardenAdInitOptions`), so passing `childDirected` /
      // `maxAdContentRating` here was, at best, redundant — and at
      // worst, the exact point where the regime could be relaxed.
      await next.init();
      setProvider(() => next);
    })();
  });

  const gate = (placementId: CashsyncPlacementId, taskInFlight?: boolean): CashsyncAdDecision => {
    const def = placementDef(CASHSYNC_AD_PLACEMENTS, placementId);
    if (!def || !live()) {
      // An undeclared placement does not exist: nothing is rendered.
      return { allow: false, reason: "premium" };
    }
    const editingAmount = amountEditors() > 0;
    // ① HARD policy of the money domain. It goes first: in a
    //    finance app, "not here" beats any frequency schedule.
    const policy = evaluateCashsyncPlacement(placementId, { amountBeingEdited: editingAmount });
    if (!policy.allowed) {
      return { allow: false, reason: policy.reason };
    }
    // ② Capping/breakpoints from the generic kit.
    return adGate({
      kind: def.kind,
      placementId,
      breakpoint: def.breakpoint,
      rewardType: def.reward?.type,
      now: Date.now(),
      caps: caps.snapshot(),
      session: sessionSignals(),
      user: { premium: premium(), child: adsConfig().childDirected },
      inFlight: taskInFlight ?? (inFlight() || editingAmount),
      // ③ CONFIG_OVER_CODE: frequency capping is decided by the platform
      //    (`ads.*`), not by a literal. With nothing configured, `appConfig()` serves
      //    code defaults that ARE `DEFAULT_AD_CAP_POLICY` byte for byte.
      //
      //    Watch the ORDER: this overrides frequencies, never surface policy.
      //    ① has already decided "not here" and there is no going back: no
      //    configuration value can slip an ad next to an editable amount.
      //
      //    And within frequencies, it can only HARDEN: `adGate` combines
      //    this with `strictestAdCapPolicy`, so an `ads.interstitial_per_day`
      //    higher than the regime's is ignored. Same rule as the regime clamp
      //    and `hardenAdInitOptions`: config tightens, never loosens.
      policy: adCapPolicyOverrides(appConfig()),
    });
  };

  const showKind = async (
    placementId: CashsyncPlacementId,
    kind: AdKind,
  ): Promise<{ shown: boolean; reward?: AdReward }> => {
    if (!gate(placementId).allow) {
      return { shown: false };
    }
    const p = provider();
    if (!(await p.prepare(kind, placementId))) {
      // No fill: the UI collapses the gap. Never a spinner waiting for an ad.
      return { shown: false };
    }
    return p.show(kind, placementId);
  };

  const value: AdsContextValue = {
    config: adsConfig,
    live,
    premium,
    provider,
    gate,
    completeValueUnit: () => setUnitsCompleted((n) => n + 1),
    setInFlight,
    setAmountBeingEdited: (v) => setAmountEditors((n) => Math.max(0, v ? n + 1 : n - 1)),
    rewards,
    exportBudget,
    rewardsVersion,
    exportAllowance: () => {
      rewardsVersion();
      return evaluateExportAllowance({
        adsLive: live(),
        premium: premium(),
        rewards,
        budget: exportBudget,
      });
    },
    recordExport: () => {
      exportBudget.record();
      setRewardsVersion((n) => n + 1);
    },
    recordOffer: (placementId) => caps.recordOffer(placementId),
    setScreenCounts: (counts) => caps.setScreenCounts(counts),
    showInterstitial: async (placementId) => {
      const result = await showKind(placementId, "interstitial");
      if (result.shown) {
        caps.recordInterstitial();
      }
      return result.shown;
    },
    showRewarded: async (placementId) => {
      const def = placementDef(CASHSYNC_AD_PLACEMENTS, placementId);
      const result = await showKind(placementId, "rewarded");
      // The provider's `reward` only CERTIFIES that the user completed the
      // ad; watching halfway earns nothing. WHAT is granted ALWAYS comes from
      // the placement registry, never from the network payload (which is
      // external input). If the reward requires the backend, the server verifies it again.
      const granted = result.reward ? rewards.grant(def?.reward) : null;
      if (granted) {
        caps.recordReward({ type: granted.type, amount: granted.amount }, placementId);
        setRewardsVersion((n) => n + 1);
        return { type: granted.type, amount: granted.amount };
      }
      return null;
    },
  };

  return <AdsContext.Provider value={value}>{props.children}</AdsContext.Provider>;
};

/**
 * Access to the ads context. Returns `undefined` if no provider is mounted
 * — intentionally: an app without ads (tiers C/D) must not fail for not mounting it.
 */
export function useAds(): AdsContextValue | undefined {
  return useContext(AdsContext);
}

/**
 * Declares, while the component is mounted, that there is an **amount being
 * edited** on screen. This hooks the `ads-policy` "never next to editable
 * amounts" rule to the real UI: transaction creation and editing dialogs,
 * transfers, budgets, debts, and balance adjustments use it.
 */
export function useAmountEditingGuard(): void {
  const ads = useAds();
  ads?.setAmountBeingEdited(true);
  onCleanup(() => {
    ads?.setAmountBeingEdited(false);
  });
}
